import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  NearestStationModel,
  NearestStationRequest,
} from '../../models/nearest-station';
import { NearestStationAdapter } from '../../adapters/nearest-station';
import { StationsModel } from '../../../../../stations/models/stations';
import { StationsAdapter } from '../../../../../stations/adapters/stations';
import { StationDetailModel } from '../../models/station-detail';
import { StationDetailAdapter } from '../../adapters/station-detail';
import { MonthlyHistoryModel } from '../../models/monthly-history';
import { MonthlyHistoryAdapter } from '../../adapters/monthly-history';
import { MonthlyAverageModel } from '../../models/monthly-average';
import { MonthlyAverageAdapter } from '../../adapters/monthly-average';
import { environment } from '@env/environment';

const BASE_URL = environment.apiBaseUrl + '/weather-station/stations';

/**
 * Serviço centralizado para todas as APIs utilizadas na Home Page
 */
@Injectable({
  providedIn: 'root',
})
export class HomePageApiService {
  constructor(private readonly http: HttpClient) {}

  /**
   * Busca todas as estações meteorológicas
   * Utilizado para popular o dropdown de seleção de estações em "Dados Meteorológicos"
   * @returns Observable com array de todas as estações
   */
  getAllStations(): Observable<StationsModel[]> {
    return this.http
      .get<any>(BASE_URL)
      .pipe(map((response: any) => response.data.map(StationsAdapter.fromApi)));
  }

  /**
   * Busca a estação meteorológica mais próxima com base nas coordenadas fornecidas
   * @param request Objeto contendo latitude e longitude do usuário
   * @returns Observable com os dados completos da estação mais próxima
   */
  getNearestStation(
    request: NearestStationRequest,
  ): Observable<NearestStationModel> {
    // Formata as coordenadas para o padrão aceito pela API
    const formattedRequest = this.formatCoordinates(request);

    return this.http.post<any>(`${BASE_URL}/nearest`, formattedRequest).pipe(
      map((response: any) => {
        if (response?.data) {
          return NearestStationAdapter.fromApi(response.data);
        }
        throw new Error('Resposta da API inválida');
      }),
    );
  }

  /**
   * Busca apenas o identifier da estação mais próxima
   * @param request Objeto contendo latitude e longitude do usuário
   * @returns Observable com o identifier da estação (ex: "9011")
   */
  getNearestStationIdentifier(
    request: NearestStationRequest,
  ): Observable<string> {
    return this.getNearestStation(request).pipe(
      map((station: NearestStationModel) => station.identifier),
    );
  }

  /**
   * Busca uma estação meteorológica específica por ID
   * @param id ID da estação a ser buscada
   * @returns Observable com os dados detalhados da estação
   */
  getStationById(id: number | string): Observable<StationDetailModel> {
    return this.http.get<any>(`${BASE_URL}/${id}`).pipe(
      map((response: any) => {
        if (response?.data) {
          return StationDetailAdapter.fromApi(response.data);
        }
        throw new Error('Resposta da API inválida');
      }),
    );
  }

  /**
   * Formata as coordenadas geográficas para o padrão aceito pela API
   * Latitude: máximo 2 dígitos antes do ponto e 7 dígitos decimais
   * Longitude: máximo 3 dígitos antes do ponto e 7 dígitos decimais
   * @param request Coordenadas originais
   * @returns Coordenadas formatadas
   */
  private formatCoordinates(
    request: NearestStationRequest,
  ): NearestStationRequest {
    return {
      latitude: this.truncateDecimal(request.latitude, 7),
      longitude: this.truncateDecimal(request.longitude, 7),
    };
  }

  /**
   * Busca a média histórica mensal de uma estação meteorológica
   * @param stationId ID da estação meteorológica
   * @param month Mês do ano (1-12)
   * @returns Observable com os dados de média histórica mensal
   */
  getMonthlyHistory(
    stationId: number,
    month: number,
  ): Observable<MonthlyHistoryModel> {
    const url = `${environment.apiBaseUrl}/weather-station/data/average/monthly-history/${stationId}?month=${month}`;

    // Adiciona header para indicar que o erro será tratado localmente
    const headers = { 'X-Handle-Error-Locally': 'true' };

    return this.http.get<any>(url, { headers }).pipe(
      map((response: any) => {
        if (response?.data) {
          return MonthlyHistoryAdapter.fromApi(response.data);
        }
        throw new Error('Resposta da API inválida');
      }),
    );
  }

  /**
   * Busca a média mensal dos dados diários de uma estação meteorológica
   * @param stationId ID da estação meteorológica
   * @param month Mês do ano (1-12)
   * @param year Ano (ex: 2026)
   * @returns Observable com os dados de média mensal
   */
  getMonthlyAverage(
    stationId: number,
    month: number,
    year: number,
  ): Observable<MonthlyAverageModel> {
    const url = `${environment.apiBaseUrl}/weather-station/data/average/monthly/${stationId}?month=${month}&year=${year}`;

    // Adiciona header para indicar que o erro será tratado localmente
    const headers = { 'X-Handle-Error-Locally': 'true' };

    return this.http.get<any>(url, { headers }).pipe(
      map((response: any) => {
        if (response?.data) {
          return MonthlyAverageAdapter.fromApi(response.data);
        }
        throw new Error('Resposta da API inválida');
      }),
    );
  }

  /**
   * Trunca um número para o número especificado de casas decimais
   * @param value Valor a ser truncado
   * @param decimals Número de casas decimais
   * @returns Valor truncado
   */
  private truncateDecimal(value: number, decimals: number): number {
    const multiplier = Math.pow(10, decimals);
    return Math.trunc(value * multiplier) / multiplier;
  }
}
